import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { ClipLoader } from "react-spinners";
import Button from "@material-ui/core/Button";
import fetch from "../../util/api-ajax";
import * as constants from "../../../constants";

/**
 * GX-7302 — read-only ops view of a member's battery-project journey, shown in
 * the "Project Hub" tab on Member Details. Mirrors what the customer sees in the
 * Project Hub (the 6-phase pipeline) but for admin/support: dense, date-driven,
 * with the underlying crm-service deal fields visible.
 *
 * Data comes from crm-service through the existing api.dev3 gateway with the
 * admin's own token (unscoped): /crm/deal/search by member email -> /crm/deal/get.
 * The journey fields are Battery_Projects-sourced (GX-7265), exposed on the deal.
 * Self-contained: component-local fetch + state, no redux plumbing.
 *
 * Not the hub's exact phasesFor derivation — a lightweight, end-backwards ops
 * mapping of the raw fields. Blanks are shown honestly, never an invented date.
 */

const BRAND = "#ED0874";
const C = {
  ink: "#202124", inkSoft: "#585858", inkFaint: "#8a8f98",
  line: "#e3e5e8", lineStrong: "#cccccc", panel: "#ffffff", panel2: "#fbfbfa", primary: "#3f51b5",
  done: "#43a047", doneSoft: "#e7f4e8",
  wait: "#e65100", waitSoft: "#fdeade",
  up: "#9aa0a6", upSoft: "#eef0f2",
  exc: "#d32f2f", excSoft: "#fce8e6",
  brand: BRAND, brandSoft: "#fde4f1",
  mono: '"Roboto Mono", ui-monospace, Menlo, Consolas, monospace',
};

// --- branch-flag chip icons (inline SVG, CSP-safe) ---
const IconDoc = (stroke) => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M9 13h6M9 17h4" />
  </svg>
);
const IconHome = (stroke) => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><path d="M9 22V12h6v10" />
  </svg>
);
const IconAlert = (stroke) => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><path d="M12 9v4M12 17h.01" />
  </svg>
);

/** A branch-flag chip: tone 'on' (applies), 'off' (struck value), 'muted' (neutral, e.g. unknown), 'alert' (red). */
function FlagChip({ icon, label, value, tone }) {
  const c = tone === "alert" ? { bg: C.excSoft, bd: C.exc, fg: C.exc, st: C.exc }
    : tone === "on" ? { bg: C.panel2, bd: "#c7cdd6", fg: C.ink, st: C.primary }
    : { bg: C.panel2, bd: C.line, fg: C.inkFaint, st: C.up };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: tone === "alert" ? 700 : 500, padding: "4px 10px 4px 8px", borderRadius: 999, border: `1px solid ${c.bd}`, background: c.bg, color: c.fg }}>
      {icon(c.st)}
      <span>{label}{value ? <span style={{ textDecoration: tone === "off" ? "line-through" : "none", textDecorationColor: C.up }}> {value}</span> : null}</span>
    </span>
  );
}
FlagChip.propTypes = {
  icon: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  tone: PropTypes.string,
};

/** Short date, blank-honest. Accepts ISO / date strings; "" or falsy -> null. */
function fmtDate(v) {
  if (!v || typeof v !== "string") return null;
  // Parse a bare YYYY-MM-DD as *local* midnight, not UTC — otherwise
  // toLocaleDateString renders it a day early for admins west of UTC.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.trim());
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(v);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
function isSet(v) { return typeof v === "string" ? v.trim() !== "" : Boolean(v); }

/** Lightweight, end-backwards derivation of the 6-phase journey from the deal. */
function deriveJourney(d) {
  const surveyRequired = d.site_survey_required === true || d.site_survey_required === "true";
  // permit_required isn't carried on the deal DTO, so keep three honest states:
  // an explicit true/false from the deal, else "required" when a permit signal
  // exists, else "unknown" — never assert "not required" from mere absence.
  const permitExplicit = (d.permit_required === true || d.permit_required === "true") ? "required"
    : (d.permit_required === false || d.permit_required === "false") ? "not-required" : null;
  const permitSignals = isSet(d.permit_status) || isSet(d.permit_submitted_on) || isSet(d.permit_approved_on);
  const permitState = permitExplicit || (permitSignals ? "required" : "unknown");
  const showPermitting = permitState !== "not-required"; // keep the phase visible unless explicitly not required

  const vss = String(d.vss_status || "").trim().toLowerCase();
  const permit = String(d.permit_status || "").toLowerCase();
  const pto = String(d.pto_status || "").toLowerCase();

  const contractDate = d.battery_agreement_signed_at;
  const approvalDate = d.project_approval_date;
  // Live permit_status values vary ("Approved - Uploaded", "Submitted", ...), so
  // match by substring — but exclude negations ("Not Approved", "Not Submitted")
  // so a negated status can't read as done.
  const permitNegated = /\bnot\b|denied|reject/.test(permit);
  const permitApproved = isSet(d.permit_approved_on) || (/approved/.test(permit) && !permitNegated) || /not.?required/.test(permit);
  const permitSubmitted = isSet(d.permit_submitted_on) || permitApproved || (/submit|review|upload|pending|progress/.test(permit) && !permitNegated);
  const installScheduled = d.installation_scheduled;
  const installDone = d.installation_completed;
  const ptoGranted = d.pto_granted_date;
  const ptoException = /exception/.test(pto);

  // Current phase as a canonical key, derived end-backwards. Later phases gate
  // on earlier completion, so a stray "permit approved" alone can't jump the
  // journey past Confirmation. Resolving to a key (rather than a fixed 0..5
  // index) keeps it aligned with `order`, which omits "permitting" only when a
  // permit is explicitly not required — a numeric index would otherwise point at
  // the wrong phase, or past the end, in the no-permit path.
  let currentKey = "contract";
  if (isSet(contractDate)) currentKey = "confirmation";           // Project Confirmation
  if (isSet(approvalDate)) {                                       // confirmation complete
    currentKey = showPermitting ? "permitting" : "installation";  // Permitting, else Installation
    if (permitApproved) currentKey = "installation";              // permit cleared -> Installation
  }
  if (isSet(installScheduled)) currentKey = "installation";       // scheduled implies at Installation
  if (isSet(installDone)) currentKey = "pto";                     // PTO
  if (isSet(ptoGranted)) currentKey = "active";                   // Active on Flow

  const step = (label, sub, status, date) => ({ label, sub, status, date: fmtDate(date) });

  // Once the project is approved, Project Confirmation is complete — infer its
  // sub-steps done even when the individual status/date fields aren't populated
  // (a further-along project's confirmation fields are often sparse on Zoho).
  const pastConfirmation = isSet(approvalDate);

  const confSteps = [
    step("Welcome call", "Welcome_Call_Status",
      isSet(d.welcome_call_completed_at) || /complete/i.test(d.welcome_call_status || "") || pastConfirmation ? "done" : (isSet(d.welcome_call_status) ? "wait" : "up"),
      d.welcome_call_completed_at),
    step("Home Snapshot", "Virtual_Survey_Status",
      vss === "rejected" ? "exc" : (vss === "approved" || pastConfirmation ? "done" : (isSet(d.vss_status) ? "wait" : "up")),
      d.vss_status_update_date),
  ];
  if (surveyRequired) {
    confSteps.push(step("On-site survey", "Site_Survey_Status",
      isSet(d.site_survey_completed_date) || pastConfirmation ? "done" : (isSet(d.site_survey_start_time) ? "wait" : "up"),
      d.site_survey_completed_date || d.site_survey_start_time));
  } else {
    confSteps.push(step("On-site survey", "Survey_Required = false", "na", null));
  }
  confSteps.push(step("Plan review", "Plan_Set → Project_Approval_Date", isSet(approvalDate) ? "done" : "up", approvalDate));

  const phases = [
    { key: "contract", name: "Contract Signed", date: fmtDate(contractDate),
      steps: [step("Battery agreement signed", "Battery_Agreement_Signed_At", isSet(contractDate) ? "done" : "up", contractDate)] },
    { key: "confirmation", name: "Project Confirmation", date: fmtDate(approvalDate), steps: confSteps },
  ];
  if (showPermitting) {
    phases.push({ key: "permitting", name: "Permitting", date: fmtDate(d.permit_approved_on), steps: [
      step("Permit submitted", "Permit_Submitted_On", permitSubmitted ? "done" : "up", d.permit_submitted_on),
      step("Permit approval", "Permit_Status · Permit_Approved_On", permitApproved ? "done" : (permitSubmitted ? "wait" : "up"), d.permit_approved_on),
    ]});
  }
  phases.push({ key: "installation", name: "Installation", date: fmtDate(installDone), steps: [
    step("Install scheduled", "Installation_Scheduled_For", isSet(installScheduled) ? "done" : "up", installScheduled),
    step("Install completed", "Installation_Completed_On", isSet(installDone) ? "done" : "up", installDone),
  ]});
  phases.push({ key: "pto", name: "PTO", date: fmtDate(d.pto_granted_date), steps: [
    step("Interconnection", "Interconnection_Status · Approved_On", isSet(ptoGranted) ? "done" : (ptoException ? "exc" : "up"), ptoGranted),
  ]});
  phases.push({ key: "active", name: "Active on Flow", date: fmtDate(ptoGranted), steps: [
    step("Energized", "Interconnection_Approved_On", isSet(ptoGranted) ? "done" : "up", ptoGranted),
  ]});

  // phase state by position vs current (current derived from the key, so it
  // always indexes into `order` — whether or not the permit phase is present)
  const order = ["contract", "confirmation", showPermitting ? "permitting" : null, "installation", "pto", "active"].filter(Boolean);
  const current = order.indexOf(currentKey);
  phases.forEach((p) => {
    const idx = order.indexOf(p.key);
    p.state = idx < current ? "done" : idx === current ? "current" : "upcoming";
  });

  // "where it is now" summary. The default covers currentKey === "contract",
  // i.e. nothing is signed yet — don't claim the contract is signed.
  let nowTitle = "Not started yet", nowBody = "No signed battery agreement on the deal.", who = "customer", whoText = "awaiting contract";
  if (ptoException) { nowTitle = "Interconnection — exception"; nowBody = "Utility flagged an exception; needs ops follow-up."; who = "ops"; whoText = "needs attention"; }
  else if (currentKey === "active") { nowTitle = "Active on Flow"; nowBody = "Energized — the battery is covering the home."; who = "done"; whoText = ""; } // the "Complete" tag says it; no redundant sub-label
  else if (currentKey === "pto") { nowTitle = "PTO — interconnection"; nowBody = "Awaiting utility permission to operate."; who = "us"; whoText = "on the utility"; }
  else if (currentKey === "installation") { nowTitle = isSet(installScheduled) ? "Installation — scheduled" : "Installation — ready to schedule"; nowBody = isSet(installScheduled) ? "Install is on the calendar." : "Cleared to schedule the install."; who = "us"; whoText = isSet(installScheduled) ? "install booked" : "scheduling"; }
  else if (currentKey === "permitting") { nowTitle = permitApproved ? "Permitting — approved" : (isSet(d.permit_submitted_on) ? "Permitting — permit under review" : "Permitting — preparing"); nowBody = "Permits and planning are on us — nothing waiting on the customer."; who = "us"; whoText = "no customer action"; }
  else if (currentKey === "confirmation") {
    // R11 (GX-7438): a snapshot that is "In Review" / "Under Review" in Zoho is
    // on US to review, not waiting on the customer. Match resiliently across
    // casing, spacing, underscores and hyphens (Zoho picklist values drift), and
    // exclude a negated value ("Not In Review") — mirroring the permit_status
    // negation guard above. `vss` is already trimmed + lowercased.
    const inReview = !/\bnot\b/.test(vss) && /\b(?:in|under)[\s_-]*review\b/.test(vss);
    const snapshotOpen = vss !== "approved" && !inReview;
    nowTitle = snapshotOpen ? "Project Confirmation — Home Snapshot" : "Project Confirmation — in review";
    nowBody = snapshotOpen ? "Waiting on the customer's Home Snapshot." : "Reviewing the customer's submission.";
    who = snapshotOpen ? "customer" : "us"; whoText = snapshotOpen ? "waiting on customer" : "in review";
  }

  return { phases, order, current, now: { title: nowTitle, body: nowBody, who, whoText },
    flags: { surveyRequired, permitState, disqualified: /reject|disqualif/i.test(vss) || /disqualif/i.test(String(d.status || "")), exception: ptoException } };
}

// ---- raw field list for the ops panel (deal/get keys we read) ----
// A few keys (permit_required, plan_set_ready, site_survey_status) aren't always
// on the DTO and will render "—"; kept deliberately to document the fields the
// journey derivation looks for.
const RAW_FIELDS = [
  "status", "stage", "battery_agreement_signed_at", "welcome_call_status", "welcome_call_completed_at",
  "vss_status", "vss_status_update_date", "site_survey_required", "site_survey_status",
  "site_survey_start_time", "site_survey_completed_date", "plan_set_ready", "project_approval_date",
  "permit_required", "permit_status", "permit_submitted_on", "permit_approved_on",
  "installation_scheduled", "installation_completed", "pto_status", "pto_granted_date",
];

const PILL = {
  done: { bg: C.doneSoft, fg: C.done, label: "Done" },
  wait: { bg: C.waitSoft, fg: C.wait, label: "Waiting" },
  up: { bg: C.upSoft, fg: C.up, label: "Upcoming" },
  exc: { bg: C.excSoft, fg: C.exc, label: "Exception" },
  na: { bg: C.upSoft, fg: C.inkFaint, label: "Not required" },
};
function Pill({ status, text }) {
  const p = PILL[status] || PILL.up;
  return <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".02em", textTransform: "uppercase", padding: "3px 9px", borderRadius: 999, whiteSpace: "nowrap", background: p.bg, color: p.fg }}>{text || p.label}</span>;
}
Pill.propTypes = { status: PropTypes.string, text: PropTypes.string };

export default function MemberProjectHub({ member, adminUser, onOpenHub }) {
  const [state, setState] = useState({ loading: true, error: null, deal: null });
  const email = member && member.get ? member.get("email") : "";
  const userID = member && member.get ? member.get("userID") : "";

  useEffect(() => {
    let alive = true;
    // Reset on member change so a previous member's deal never lingers on screen
    // while the new one loads, and so the spinner (not an empty state) shows.
    setState({ loading: true, error: null, deal: null });
    const wanted = String(email || "").trim().toLowerCase();
    (async () => {
      try {
        if (!wanted) { if (alive) setState({ loading: false, error: "no-email", deal: null }); return; }
        const search = await fetch(constants.CRM_DEAL_SEARCH_URL, { search: email });
        const deals = (search && (search.deals || search.Deals)) || [];
        // /crm/deal/search is free-text: when results carry an email, keep only
        // exact (case-insensitive) matches so another customer's deal can't be
        // chosen; pick deterministically (most recently signed) if several match.
        const withEmail = deals.filter((x) => isSet(x.email));
        const pool = (withEmail.length ? withEmail.filter((x) => x.email.trim().toLowerCase() === wanted) : deals).slice();
        pool.sort((a, b) => String(b.battery_agreement_signed_at || "").localeCompare(String(a.battery_agreement_signed_at || "")));
        const chosen = pool[0];
        if (!chosen) { if (alive) setState({ loading: false, error: null, deal: null }); return; }
        const resp = await fetch(constants.CRM_DEAL_GET_URL, { deal_id: chosen.id || chosen.deal_id });
        const deal = resp && (resp.deal || resp.Deal);
        // Definitive guard: never render a deal whose email isn't this member's.
        if (deal && isSet(deal.email) && deal.email.trim().toLowerCase() !== wanted) {
          if (alive) setState({ loading: false, error: null, deal: null });
          return;
        }
        if (alive) setState({ loading: false, error: deal ? null : "not-found", deal: deal || null });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("MemberProjectHub: crm lookup failed", e);
        const denied = !!(e && (e.code === 7 || /permission|denied|forbidden/i.test(e.error || e.message || "")));
        if (alive) setState({ loading: false, error: denied ? "forbidden" : "fetch", deal: null });
      }
    })();
    return () => { alive = false; };
  }, [email]);

  const j = useMemo(() => (state.deal ? deriveJourney(state.deal) : null), [state.deal]);

  const canImpersonate = adminUser && adminUser.permissions && adminUser.permissions.isHubImpersonate && process.env.REACT_APP_HUB_APP_URL;
  const OpenHubBtn = canImpersonate ? (
    <Button variant="contained" color="primary" style={{ marginLeft: "auto" }} onClick={() => onOpenHub && onOpenHub(userID, email)}>
      Open Hub ↗
    </Button>
  ) : null;

  if (state.loading) {
    return <div style={{ display: "flex", justifyContent: "center", padding: 60 }}><ClipLoader sizeUnit="px" size={64} color={BRAND} loading /></div>;
  }
  if (state.error === "no-email") {
    return <Empty msg="No email on this member — can't resolve the battery project." />;
  }
  if (state.error === "forbidden") {
    return <Empty msg="You don't have permission to view this member's project (crm-service denied the request)." tone="error" />;
  }
  if (state.error === "fetch") {
    return <Empty msg="Couldn't load the project from crm-service. Try again." tone="error" action={OpenHubBtn} />;
  }
  if (!state.deal || state.error === "not-found") {
    return <Empty msg="No battery project found for this member." action={OpenHubBtn} />;
  }

  const font = { fontFamily: '"Roboto", system-ui, "Helvetica Neue", Arial, sans-serif', color: C.ink };
  const sec = { fontSize: 11, letterSpacing: ".1em", textTransform: "uppercase", color: C.inkFaint, fontWeight: 700, margin: "18px 2px 10px" };

  const whoTag = j.now.who === "customer"
    ? { bg: C.waitSoft, fg: C.wait }
    : j.now.who === "ops" ? { bg: C.excSoft, fg: C.exc }
    : j.now.who === "done" ? { bg: C.doneSoft, fg: C.done }
    : { bg: C.upSoft, fg: C.inkSoft };

  return (
    <div style={{ ...font, padding: "4px 2px 12px" }}>
      {/* header row — identity left, branch flags + Open Hub right */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, paddingBottom: 14, borderBottom: `1px solid ${C.line}` }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 500 }}>{`${member.get("first_name") || ""} ${member.get("last_name") || ""}`.trim() || "Member"}</div>
          <div style={{ fontFamily: C.mono, fontSize: 11.5, color: C.inkFaint, marginTop: 3 }}>
            {email || "no email"}{state.deal.state ? ` · ${state.deal.state}` : ""}{state.deal.num_of_batteries ? ` · ${state.deal.num_of_batteries} batteries` : ""}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginLeft: "auto" }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "flex-end", gap: 8 }}>
            <FlagChip icon={IconDoc} label="Permit"
              value={j.flags.permitState === "required" ? "required" : j.flags.permitState === "not-required" ? "not required" : "unknown"}
              tone={j.flags.permitState === "required" ? "on" : j.flags.permitState === "not-required" ? "off" : "muted"} />
            <FlagChip icon={IconHome} label="Site survey" value={j.flags.surveyRequired ? "required" : "not required"} tone={j.flags.surveyRequired ? "on" : "off"} />
            {j.flags.disqualified ? <FlagChip icon={IconAlert} label="Disqualified" tone="alert" /> : null}
            {j.flags.exception ? <FlagChip icon={IconAlert} label="Interconnection exception" tone="alert" /> : null}
          </div>
          {OpenHubBtn}
        </div>
      </div>

      {/* now banner */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "15px 18px", borderRadius: 6, background: C.brandSoft, borderLeft: `4px solid ${C.brand}`, margin: "18px 0" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10.5, letterSpacing: ".1em", textTransform: "uppercase", color: C.brand, fontWeight: 700 }}>Where the project is now</div>
          <div style={{ fontSize: 17, fontWeight: 500, margin: "3px 0" }}>{j.now.title}</div>
          <div style={{ fontSize: 13, color: C.inkSoft }}>{j.now.body}</div>
        </div>
        <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
          <span style={{ fontWeight: 700, fontSize: 11, padding: "3px 10px", borderRadius: 999, textTransform: "uppercase", letterSpacing: ".03em", background: whoTag.bg, color: whoTag.fg }}>{j.now.who === "us" ? "On us" : j.now.who === "customer" ? "On customer" : j.now.who === "done" ? "Complete" : "Attention"}</span>
          {j.now.whoText ? <div style={{ marginTop: 6, fontFamily: C.mono, fontSize: 11.5, color: C.inkSoft }}>{j.now.whoText}</div> : null}
        </div>
      </div>

      {/* journey — compact connected track */}
      <div style={sec}>Journey</div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${j.phases.length}, 1fr)`, background: C.panel2, border: `1px solid ${C.line}`, borderRadius: 6, padding: "14px 8px 12px" }}>
        {j.phases.map((p, i) => {
          const short = p.name === "Contract Signed" ? "Contract" : p.name === "Project Confirmation" ? "Confirmation" : p.name === "Active on Flow" ? "Active" : p.name;
          return (
            <div key={p.key} style={{ position: "relative", textAlign: "center", padding: "0 2px" }}>
              {i > 0 ? <div style={{ position: "absolute", top: 11, left: "-50%", width: "100%", height: 2, background: p.state === "done" || p.state === "current" ? C.done : C.lineStrong, zIndex: 0 }} /> : null}
              <div style={{ position: "relative", zIndex: 1, width: 24, height: 24, borderRadius: "50%", margin: "0 auto", display: "grid", placeItems: "center", fontSize: 12, fontWeight: 700,
                border: `2px solid ${p.state === "done" ? C.done : p.state === "current" ? C.brand : C.lineStrong}`,
                background: p.state === "done" ? C.done : C.panel,
                color: p.state === "done" ? "#fff" : p.state === "current" ? C.brand : C.inkFaint,
                boxShadow: p.state === "current" ? `0 0 0 3px ${C.brandSoft}` : "none" }}>{p.state === "done" ? "✓" : i + 1}</div>
              <div style={{ fontSize: 10.5, fontWeight: p.state === "upcoming" ? 400 : 500, marginTop: 6, color: p.state === "upcoming" ? C.inkFaint : C.ink, lineHeight: 1.2 }}>{short}</div>
              <div style={{ fontFamily: C.mono, fontSize: 9.5, color: p.state === "current" ? C.brand : C.inkFaint, marginTop: 1, fontWeight: p.state === "current" ? 700 : 400 }}>{p.date || (p.state === "current" ? "in progress" : "—")}</div>
            </div>
          );
        })}
      </div>

      {/* phase detail */}
      <div style={sec}>Steps &amp; dates</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {j.phases.map((p) => (
          <div key={p.key} style={{ background: C.panel, border: `1px solid ${p.state === "current" ? C.brand : C.line}`, borderRadius: 6, overflow: "hidden", boxShadow: p.state === "current" ? `0 0 0 1px ${C.brand}` : "none" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", background: C.panel2, borderBottom: `1px solid ${C.line}` }}>
              <span style={{ fontSize: 14, fontWeight: 500 }}>{p.name}</span>
              <span style={{ marginLeft: 8 }}><Pill status={p.state === "done" ? "done" : p.state === "current" ? "wait" : "up"} text={p.state === "done" ? "Done" : p.state === "current" ? "In progress" : "Upcoming"} /></span>
              <span style={{ marginLeft: "auto", fontFamily: C.mono, fontSize: 11.5, color: C.inkFaint }}>{p.date || "—"}</span>
            </div>
            <div>
              {p.steps.map((s, idx) => (
                <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr auto auto", alignItems: "center", gap: 12, padding: "9px 16px", borderTop: idx ? `1px solid ${C.line}` : "none", fontSize: 13 }}>
                  <span>{s.label}<span style={{ display: "block", fontSize: 11, color: C.inkFaint, fontFamily: C.mono, marginTop: 1 }}>{s.sub}</span></span>
                  <Pill status={s.status} />
                  <span style={{ fontFamily: C.mono, fontSize: 11.5, color: s.date ? C.inkSoft : C.inkFaint, minWidth: 84, textAlign: "right" }}>{s.date || "—"}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* raw data */}
      <details style={{ marginTop: 22 }}>
        <summary style={{ cursor: "pointer", fontSize: 12, fontWeight: 500, textTransform: "uppercase", letterSpacing: ".03em", color: C.inkSoft, padding: "10px 14px", background: C.panel2, border: `1px solid ${C.line}`, borderRadius: 6 }}>
          Underlying crm deal/get fields
        </summary>
        <div style={{ overflowX: "auto", border: `1px solid ${C.line}`, borderTop: "none", borderRadius: "0 0 6px 6px" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
            <tbody>
              {RAW_FIELDS.map((k, i) => {
                const v = state.deal[k];
                const empty = !isSet(v);
                return (
                  <tr key={k} style={{ background: i % 2 ? C.panel2 : C.panel }}>
                    <td style={{ padding: "7px 14px", fontFamily: C.mono, color: C.inkSoft, whiteSpace: "nowrap", width: "46%", borderTop: `1px solid ${C.line}` }}>{k}</td>
                    <td style={{ padding: "7px 14px", fontFamily: C.mono, color: empty ? C.inkFaint : C.ink, borderTop: `1px solid ${C.line}` }}>{empty ? "—" : String(v)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

function Empty({ msg, tone, action }) {
  return (
    <div style={{ textAlign: "center", padding: "48px 20px", fontFamily: '"Roboto", system-ui, Arial, sans-serif' }}>
      <div style={{ fontSize: 14, color: tone === "error" ? "#d32f2f" : "#585858" }}>{msg}</div>
      {action ? <div style={{ marginTop: 16, display: "flex", justifyContent: "center" }}>{action}</div> : null}
    </div>
  );
}
Empty.propTypes = { msg: PropTypes.string.isRequired, tone: PropTypes.string, action: PropTypes.node };

MemberProjectHub.propTypes = {
  member: PropTypes.object.isRequired,
  adminUser: PropTypes.object,
  onOpenHub: PropTypes.func,
};
